import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { DropdownOption, GlobalDropdownField } from '../../components/GlobalComponents/GlobalDropdownField';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
import { GlobalInputField } from '../../components/GlobalComponents/GlobalInputField';
import { useSuperAdminDashboardStore } from '../../stateManagers/stores/superAdminDashboardStore';

interface Branch {
  branch_id: string;
  institute_id: string;
  name: string;
  status: string;
}

interface RegisterOperatorModalProps {
  visible: boolean;
  onHide: () => void;
  onSuccess?: () => void;
  toastRef: React.RefObject<Toast | null>;
  preselectedInstituteId?: string;
  preselectedBranchId?: string;
}

const RegisterOperatorModal: React.FC<RegisterOperatorModalProps> = ({
  visible,
  onHide,
  onSuccess,
  toastRef,
  preselectedInstituteId,
  preselectedBranchId
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [branchesLoading, setBranchesLoading] = useState(false);

  // Get institutes from the dashboard store
  const { data: dashboardData, loading: dashboardLoading, fetchDashboardData } = useSuperAdminDashboardStore();
  
  // Fetch dashboard data when modal opens
  useEffect(() => {
    if (visible && !dashboardData) {
      fetchDashboardData();
    }
  }, [visible, dashboardData, fetchDashboardData]);

  const institutes = dashboardData?.institutes || [];

  const [branches, setBranches] = useState<Branch[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    institute_id: '',
    branch_id: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Set preselected values when modal opens
  useEffect(() => {
    if (visible && preselectedInstituteId) {
      setFormData(prev => ({
        ...prev,
        institute_id: preselectedInstituteId,
        branch_id: preselectedBranchId || ''
      }));
    }
  }, [visible, preselectedInstituteId, preselectedBranchId]);

  // Fetch branches when institute is selected
  useEffect(() => {
    if (formData.institute_id) {
      fetchBranches(formData.institute_id);
    } else {
      setBranches([]);
      setFormData(prev => ({ ...prev, branch_id: '' }));
    }
  }, [formData.institute_id]);

  const fetchBranches = async (instituteId: string) => {
    setBranchesLoading(true);
    try {
      const response = await apiClient.get(`/v1/branches?institute_id=${instituteId}&limit=100`);
      
      if (response.data.success) {
        setBranches(response.data.data);
      } else {
        setBranches([]);
        toastRef.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load branches',
          life: 3000
        });
      }
    } catch (err: any) {
      console.error('Failed to fetch branches:', err);
      setBranches([]);
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load branches',
        life: 3000
      });
    } finally {
      setBranchesLoading(false);
    }
  };

  const instituteOptions: DropdownOption[] = institutes.map(inst => ({
    label: inst.name,
    value: inst.institute_id
  }));

  const branchOptions: DropdownOption[] = branches
    .filter(branch => branch.status === 'active')
    .map(branch => ({
      label: branch.name,
      value: branch.branch_id
    }));

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Required fields validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.institute_id) {
      newErrors.institute_id = 'Institute is required';
    }

    if (!formData.branch_id) {
      newErrors.branch_id = 'Branch is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setError('Please fill all required fields correctly');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        institute_id: formData.institute_id,
        branch_id: formData.branch_id
      };

      // Use the combined register-and-assign endpoint
      const response = await apiClient.post('/v1/operators/register-and-assign', payload);

      if (response.data.success) {
        // Show success toast
        toastRef.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: response.data.message || 'Operator registered and assigned to branch successfully',
          life: 4000
        });

        // Close modal and trigger success callback
        handleClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setError(response.data.message || 'Failed to register operator');
        toastRef.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: response.data.message || 'Failed to register operator',
          life: 3000
        });
      }
    } catch (err: any) {
      console.error('Failed to register operator:', err);
      
      // Handle validation errors from backend
      let errorMessage = 'Failed to register operator';
      
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.messages && Array.isArray(err.response.data.messages)) {
        errorMessage = err.response.data.messages.join(', ');
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      
      // Show error toast
      toastRef.current?.show({
        severity: 'error',
        summary: 'Registration Failed',
        detail: errorMessage,
        life: 4000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      institute_id: '',
      branch_id: ''
    });
    setErrors({});
    setError(null);
    setBranches([]);
    onHide();
  };

  // Get selected institute and branch details
  const selectedInstitute = institutes.find(inst => inst.institute_id === formData.institute_id);
  const selectedBranch = branches.find(branch => branch.branch_id === formData.branch_id);

  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      style={{ width: '90vw', maxWidth: '600px' }}
      dismissableMask
      draggable={false}
      pt={{
        root: {
          style: {
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }
        }
      }}
    >
      <div>
        {/* Modal Header */}
        <div style={{
          background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: '-50px',
            right: '-50px',
            width: '150px',
            height: '150px',
            background: 'rgba(212, 241, 126, 0.1)',
            borderRadius: '50%',
            filter: 'blur(40px)'
          }} />
          
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{
              display: 'inline-block',
              padding: '4px 10px',
              background: 'rgba(212, 241, 126, 0.2)',
              borderRadius: '16px',
              marginBottom: '8px',
              fontSize: '0.65rem',
              fontWeight: '600',
              letterSpacing: '0.5px',
              textTransform: 'uppercase'
            }}>
              Operator Management
            </div>
            <h2 style={{
              margin: '0 0 6px 0',
              fontSize: '1.4rem',
              fontWeight: '700',
              color: 'white'
            }}>
              Register New Operator
            </h2>
            <p style={{
              margin: '0',
              color: 'rgba(255,255,255,0.8)',
              fontSize: '0.8rem'
            }}>
              Create and assign operator account to a branch
            </p>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <Message
            severity="error"
            text={error}
            style={{ marginBottom: '20px', fontSize: '0.9rem', fontWeight: '500' }}
          />
        )}

        {/* Loading State */}
        {dashboardLoading && (
          <div style={{
            background: '#f0f9ff',
            border: '1px solid #0f3d3e20',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#0f3d3e' }}></i>
            <p style={{ marginTop: '12px', color: '#666' }}>Loading institutes...</p>
          </div>
        )}

        {/* No Institute Warning */}
        {!dashboardLoading && institutes.length === 0 && (
          <Message
            severity="warn"
            text="No institutes found. Please create an institute first before registering operators."
            style={{ marginBottom: '20px' }}
          />
        )}

        {/* Assignment Preview */}
        {!dashboardLoading && (selectedInstitute || selectedBranch) && (
          <div style={{
            background: '#f0f9ff',
            border: '1px solid #0f3d3e20',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <p style={{
              margin: '0 0 8px 0',
              fontSize: '0.75rem',
              color: '#666',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Assignment Preview
            </p>
            {selectedInstitute && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: selectedBranch ? '12px' : '0'
              }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  background: '#0f3d3e',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <i className="pi pi-building" style={{ color: '#d4f17e', fontSize: '0.9rem' }}></i>
                </div>
                <div>
                  <p style={{ margin: '0 0 2px 0', fontSize: '0.75rem', color: '#666' }}>Institute</p>
                  <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: '#0f3d3e' }}>
                    {selectedInstitute.name}
                  </p>
                </div>
              </div>
            )}
            {selectedBranch && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                paddingTop: selectedInstitute ? '12px' : '0',
                borderTop: selectedInstitute ? '1px dashed #0f3d3e30' : 'none'
              }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  background: '#1a5555',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <i className="pi pi-map-marker" style={{ color: '#d4f17e', fontSize: '0.9rem' }}></i>
                </div>
                <div>
                  <p style={{ margin: '0 0 2px 0', fontSize: '0.75rem', color: '#666' }}>Branch</p>
                  <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: '#0f3d3e' }}>
                    {selectedBranch.name}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Form Content */}
        {!dashboardLoading && institutes.length > 0 && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px',
            border: '1px solid #f0f0f0',
            maxHeight: '50vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Personal Information Section */}
              <div style={{
                padding: '16px',
                background: '#f9f9f9',
                borderRadius: '8px',
                border: '1px solid #e0e0e0'
              }}>
                <h3 style={{
                  margin: '0 0 16px 0',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  color: '#0f3d3e'
                }}>
                  Personal Information
                </h3>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <GlobalInputField
                    label="Full Name"
                    required
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="e.g., John Doe"
                    icon="pi pi-user"
                    error={errors.name}
                  />

                  <GlobalInputField
                    label="Email Address"
                    required
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="e.g., john.doe@example.com"
                    icon="pi pi-envelope"
                    error={errors.email}
                  />

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <GlobalInputField
                      label="Password"
                      required
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      placeholder="Min. 6 characters"
                      icon="pi pi-lock"
                      error={errors.password}
                    />

                    <GlobalInputField
                      label="Confirm Password"
                      required
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      placeholder="Re-enter password"
                      icon="pi pi-lock"
                      error={errors.confirmPassword}
                    />
                  </div>
                </div>
              </div>

              {/* Assignment Section */}
              <div style={{
                padding: '16px',
                background: '#f9f9f9',
                borderRadius: '8px',
                border: '1px solid #e0e0e0'
              }}>
                <h3 style={{
                  margin: '0 0 16px 0',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  color: '#0f3d3e'
                }}>
                  Institute & Branch Assignment
                </h3>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <GlobalDropdownField
                    label="Select Institute"
                    required
                    value={formData.institute_id}
                    onChange={(val) => handleInputChange('institute_id', val)}
                    options={instituteOptions}
                    placeholder="Choose institute"
                    icon="pi pi-building"
                    error={errors.institute_id}
                    disabled={!!preselectedInstituteId}
                  />

                  <GlobalDropdownField
                    label="Select Branch"
                    required
                    value={formData.branch_id}
                    onChange={(val) => handleInputChange('branch_id', val)}
                    options={branchOptions}
                    placeholder={
                      !formData.institute_id 
                        ? "Select institute first" 
                        : branchesLoading 
                        ? "Loading branches..." 
                        : branchOptions.length === 0
                        ? "No active branches available"
                        : "Choose branch"
                    }
                    icon="pi pi-map-marker"
                    error={errors.branch_id}
                    disabled={(!formData.institute_id || branchesLoading) || !!preselectedBranchId}
                  />

                  {formData.institute_id && !branchesLoading && branchOptions.length === 0 && (
                    <Message
                      severity="warn"
                      text="No active branches found for this institute. Please create a branch first."
                      style={{ fontSize: '0.85rem' }}
                    />
                  )}
                </div>
              </div>

              {/* Info Message about Combined Operation */}
              <div style={{
                padding: '12px 16px',
                background: '#e3f2fd',
                borderRadius: '8px',
                border: '1px solid #90caf9',
                display: 'flex',
                gap: '10px',
                alignItems: 'flex-start'
              }}>
                <i className="pi pi-info-circle" style={{ color: '#1976d2', fontSize: '1rem', marginTop: '2px' }}></i>
                <div>
                  <p style={{
                    margin: '0 0 4px 0',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    color: '#1565c0'
                  }}>
                    Automatic Assignment
                  </p>
                  <p style={{
                    margin: 0,
                    fontSize: '0.75rem',
                    color: '#1976d2',
                    lineHeight: '1.4'
                  }}>
                    The operator will be automatically assigned to the selected branch. Any existing active operator will be deactivated.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Actions */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
          flexWrap: 'wrap'
        }}>
          <GlobalButton
            label="Cancel"
            variant="outline"
            icon="pi pi-times"
            onClick={handleClose}
            disabled={loading}
          />
          <GlobalButton
            label={loading ? 'Registering & Assigning...' : 'Register & Assign Operator'}
            variant="primary"
            icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-user-plus'}
            onClick={handleSubmit}
            disabled={loading || dashboardLoading || institutes.length === 0 || branchesLoading}
            loading={loading}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default RegisterOperatorModal;